import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import components
import GameCard from './components/GameCard';
import CategoryFilter from './components/CategoryFilter';
import GamePreviewModal from './components/GamePreviewModal';
import FeaturedGamesCarousel from './components/FeaturedGamesCarousel';
import LiveStatsBar from './components/LiveStatsBar';
import WhatsAppFloatingWidget from './components/WhatsAppFloatingWidget';

const GamesShowcaseGamingPalace = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedGame, setSelectedGame] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Mock data for games
  const allGames = [
    {
      id: 1,
      name: "Teen Patti Gold",
      category: "Live Casino",
      description: "India\'s most popular card game with live dealers and real players. Experience authentic Teen Patti with traditional rules.",
      fullDescription: "Teen Patti Gold brings you the authentic Indian card game experience with live dealers, real-time multiplayer action, and traditional betting patterns. Play with confidence knowing every game is fair and transparent.",
      image: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=400&h=300&fit=crop",
      featuredImage: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=800&h=400&fit=crop",
      previewImage: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=600&h=400&fit=crop",
      minBet: 10,
      maxWin: 100000,
      rating: 4.8,
      playerCount: 1247,
      isLive: true,
      isPopular: true,
      features: ["Live Dealers", "Multiplayer", "Chat", "Side Bets"],
      recentWinner: { name: "Rajesh K.", amount: 25000 },
      rules: [
        "Each player receives 3 cards face down",
        "Players bet based on their hand strength",
        "Best hand wins the pot",
        "Minimum bet starts from ₹10"
      ],
      strategies: [
        {
          title: "Start Conservative",
          description: "Begin with smaller bets to understand other players\' patterns before making larger wagers."
        },
        {
          title: "Read the Table",
          description: "Watch for betting patterns and player behavior to make informed decisions."
        }
      ]
    },
    {
      id: 2,
      name: "Andar Bahar Live",
      category: "Live Casino",
      description: "Traditional Indian card game with simple rules and exciting gameplay. Perfect for beginners and experts alike.",
      fullDescription: "Experience the thrill of Andar Bahar with our professional live dealers. This simple yet exciting game offers great odds and instant results, making it perfect for quick gaming sessions.",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
      featuredImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop",
      minBet: 5,
      maxWin: 50000,
      rating: 4.7,
      playerCount: 892,
      isLive: true,
      isPopular: true,
      features: ["Live Dealers", "Quick Rounds", "High RTP", "Mobile Optimized"],
      recentWinner: { name: "Priya S.", amount: 15000 },
      rules: [
        "Dealer places one card in the middle",
        "Choose Andar (left) or Bahar (right)",
        "Dealer deals cards alternately",
        "Win when matching card appears on your chosen side"
      ]
    },
    {
      id: 3,
      name: "Cricket Betting Live",
      category: "Sports Betting",
      description: "Live cricket betting with real-time odds on IPL, international matches, and domestic tournaments.",
      fullDescription: "Get the best odds on cricket matches including IPL, World Cup, and domestic tournaments. Live betting with instant updates and multiple betting options.",
      image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=300&fit=crop",
      featuredImage: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=800&h=400&fit=crop",
      minBet: 50,
      maxWin: 500000,
      rating: 4.9,
      playerCount: 2156,
      isLive: true,
      isPopular: true,
      features: ["Live Odds", "Multiple Markets", "Cash Out", "Live Streaming"],
      recentWinner: { name: "Amit P.", amount: 75000 }
    },
    {
      id: 4,
      name: "Bollywood Slots",
      category: "Slots",
      description: "Themed slot machines featuring Bollywood movies, music, and celebrities with exciting bonus rounds.",
      fullDescription: "Immerse yourself in Bollywood glamour with our themed slot machines. Featuring popular movies, songs, and celebrities with exciting bonus features and progressive jackpots.",
      image: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=400&h=300&fit=crop",
      minBet: 1,
      maxWin: 1000000,
      rating: 4.6,
      playerCount: 567,
      isLive: false,
      isPopular: false,
      features: ["Progressive Jackpot", "Bonus Rounds", "Free Spins", "Wild Symbols"],
      recentWinner: { name: "Sneha M.", amount: 45000 }
    },
    {
      id: 5,
      name: "Blackjack Premium",
      category: "Live Casino",
      description: "Classic blackjack with professional dealers and multiple betting options. Perfect for strategy lovers.",
      fullDescription: "Experience premium blackjack with professional dealers, multiple side bets, and perfect strategy guides. Ideal for both beginners learning the game and experts perfecting their skills.",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
      minBet: 25,
      maxWin: 200000,
      rating: 4.8,
      playerCount: 445,
      isLive: true,
      isPopular: false,
      features: ["Side Bets", "Insurance", "Double Down", "Split"],
      recentWinner: { name: "Vikram R.", amount: 35000 }
    },
    {
      id: 6,
      name: "Football Betting",
      category: "Sports Betting",
      description: "Comprehensive football betting covering Premier League, Champions League, and Indian Super League.",
      fullDescription: "Bet on football matches from around the world including Premier League, Champions League, La Liga, and Indian Super League with competitive odds and live betting options.",
      image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=300&fit=crop",
      minBet: 20,
      maxWin: 300000,
      rating: 4.7,
      playerCount: 1089,
      isLive: true,
      isPopular: true,
      features: ["Live Betting", "Multiple Leagues", "Accumulator Bets", "Cash Out"]
    },
    {
      id: 7,
      name: "Roulette Royal",
      category: "Live Casino",
      description: "European and American roulette with live dealers and special betting options for high rollers.",
      fullDescription: "Play European and American roulette with our professional dealers. Special VIP tables available for high rollers with exclusive betting limits and personalized service.",
      image: "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?w=400&h=300&fit=crop",
      minBet: 10,
      maxWin: 350000,
      rating: 4.5,
      playerCount: 334,
      isLive: true,
      isPopular: false,
      features: ["European & American", "VIP Tables", "Special Bets", "Statistics"]
    },
    {
      id: 8,
      name: "Lottery Mega Draw",
      category: "Lottery",
      description: "Daily and weekly lottery draws with massive jackpots and multiple prize tiers.",
      fullDescription: "Participate in daily and weekly lottery draws with life-changing jackpots. Multiple prize tiers ensure better winning chances with transparent draw processes.",
      image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=400&h=300&fit=crop",
      minBet: 10,
      maxWin: 10000000,
      rating: 4.4,
      playerCount: 2234,
      isLive: false,
      isPopular: true,
      features: ["Daily Draws", "Mega Jackpots", "Multiple Tiers", "Auto Numbers"]
    }
  ];

  // Categories with counts
  const categories = [
    { id: 'all', name: 'All Games', count: allGames?.length },
    { id: 'live-casino', name: 'Live Casino', count: allGames?.filter(g => g?.category === 'Live Casino')?.length },
    { id: 'sports-betting', name: 'Sports Betting', count: allGames?.filter(g => g?.category === 'Sports Betting')?.length },
    { id: 'slots', name: 'Slots', count: allGames?.filter(g => g?.category === 'Slots')?.length },
    { id: 'lottery', name: 'Lottery', count: allGames?.filter(g => g?.category === 'Lottery')?.length }
  ];

  // Featured games for carousel
  const featuredGames = allGames?.filter(game => game?.isPopular)?.slice(0, 3);

  // Filter games based on category and search
  const filteredGames = allGames?.filter(game => {
    const matchesCategory = activeCategory === 'all' || game?.category?.toLowerCase()?.replace(' ', '-') === activeCategory;
    const matchesSearch = game?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      game?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Sort games
  const sortedGames = [...filteredGames]?.sort((a, b) => {
    switch (sortBy) {
      case 'popularity':
        return (b?.playerCount || 0) - (a?.playerCount || 0);
      case 'rating':
        return (b?.rating || 0) - (a?.rating || 0);
      case 'minBet':
        return (a?.minBet || 0) - (b?.minBet || 0);
      case 'maxWin':
        return (b?.maxWin || 0) - (a?.maxWin || 0);
      case 'name':
        return a?.name?.localeCompare(b?.name);
      default:
        return 0;
    }
  });

  const handleWhatsAppClick = (game = null) => {
    const gameInfo = game ? `\n\nI'm interested in: ${game?.name}` : '';
    const message = encodeURIComponent(
      `Hello! I'm interested in Nitin Online Hub gaming services.${gameInfo}\n\nI'd like to know more about:\n- Available games and betting options\n- How to get started\n- Bonuses and promotions\n- Account setup process\n\nPlease provide me with detailed information and guidance.`
    );
    window.open(`https://wa.me/917704070169?text=${message}`, '_blank');
  };

  const handlePreviewClick = (game) => {
    setSelectedGame(game);
    setIsPreviewModalOpen(true);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Games Showcase - Gaming Palace | Nitin Online Hub | Nitin Online Book</title>
        <meta name="description" content="Explore premium gaming options at Nitin Online Hub and Nitin Online Book. Live casino games, sports betting, slots, and lottery with expert WhatsApp support and instant access. Nitin online book games for the best betting experience." />
        <meta name="keywords" content="Nitin Online Hub games, Nitin Online Book games, online casino, live games, sports betting, slots, lottery, teen patti, andar bahar, cricket betting, WhatsApp gaming, Nitin online book casino, Nitin betting games" />
        <meta property="og:title" content="Games Showcase - Gaming Palace | Nitin Online Hub | Nitin Online Book" />
        <meta property="og:description" content="Explore premium gaming options at Nitin Online Hub and Nitin Online Book. Live casino games, sports betting, slots, and lottery with expert WhatsApp support and instant access." />
        <meta property="og:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <meta property="og:url" content="https://nitinonlinehub.in/games-showcase-gaming-palace" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Nitin Online Hub" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Games Showcase - Gaming Palace | Nitin Online Hub | Nitin Online Book" />
        <meta name="twitter:description" content="Explore premium gaming options at Nitin Online Hub and Nitin Online Book. Live casino games, sports betting, slots, and lottery with expert WhatsApp support and instant access." />
        <meta name="twitter:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <link rel="canonical" href="https://nitinonlinehub.in/games-showcase-gaming-palace" />

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://nitinonlinehub.in/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Games",
                  "item": "https://nitinonlinehub.in/games-showcase-gaming-palace"
                }
              ]
            }
          `}
        </script>
      </Helmet>
      <Header />
      <LiveStatsBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-royal rounded-card flex items-center justify-center shadow-royal">
              <Icon name="Gamepad2" size={24} color="white" />
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-gradient-royal">
              Gaming Palace
            </h1>
          </div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Discover India's most trusted premium gaming platform with live dealers, 
            sports betting, and exclusive games. Get instant access via WhatsApp.
          </p>
        </div>

        {/* Featured Games Carousel */}
        <div className="mb-8">
          <FeaturedGamesCarousel 
            featuredGames={featuredGames}
            onWhatsAppClick={handleWhatsAppClick}
            onPreviewClick={handlePreviewClick}
          />
        </div>

        {/* Search and Controls */}
        <div className="bg-card rounded-card border border-border p-6 shadow-subtle mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-button bg-input text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Sort */}
              <div className="flex items-center space-x-2">
                <Icon name="ArrowUpDown" size={16} className="text-text-muted" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="border border-border rounded-button px-3 py-2 bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="popularity">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="minBet">Lowest Bet</option>
                  <option value="maxWin">Highest Win</option>
                  <option value="name">Name A-Z</option>
                </select>
              </div>

              {/* View Mode */}
              <div className="flex items-center space-x-1 bg-muted rounded-button p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-button transition-royal ${
                    viewMode === 'grid' ?'bg-primary text-primary-foreground' :'text-text-muted hover:text-text-primary'
                  }`}
                >
                  <Icon name="Grid3X3" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-button transition-royal ${
                    viewMode === 'list' ?'bg-primary text-primary-foreground' :'text-text-muted hover:text-text-primary'
                  }`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <CategoryFilter 
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
            gameCount={sortedGames?.length}
          />
        </div>

        {/* Games Grid/List */}
        <div className="mb-8">
          {sortedGames?.length > 0 ? (
            <div className={
              viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" :"space-y-4"
            }>
              {sortedGames?.map((game) => (
                <GameCard
                  key={game?.id}
                  game={game}
                  onWhatsAppClick={handleWhatsAppClick}
                  onPreviewClick={handlePreviewClick}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto text-text-muted mb-4" />
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                No games found
              </h3>
              <p className="text-text-secondary mb-6">
                Try adjusting your search or category filter
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Reset Filters
              </Button>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-royal rounded-card p-8 text-center text-white shadow-royal">
          <div className="max-w-2xl mx-auto">
            <Icon name="Crown" size={48} color="white" className="mx-auto mb-4" />
            <h2 className="font-heading font-bold text-3xl mb-4">
              Ready to Start Your Gaming Journey?
            </h2>
            <p className="text-white/90 text-lg mb-6">
              Join thousands of players who trust Nitin Online Hub for premium gaming experiences. 
              Get instant access and expert guidance through WhatsApp.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => handleWhatsAppClick()}
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                Start Gaming via WhatsApp
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10"
              >
                Call Gaming Expert
              </Button>
            </div>
            <p className="text-white/70 text-sm mt-4">
              Available 24/7 • Instant Setup • Expert Guidance
            </p>
          </div>
        </div>
      </main>
      {/* Game Preview Modal */}
      <GamePreviewModal
        game={selectedGame}
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onWhatsAppClick={handleWhatsAppClick}
      />
      {/* WhatsApp Floating Widget */}
      <WhatsAppFloatingWidget onWhatsAppClick={handleWhatsAppClick} />
    </div>
  );
};

export default GamesShowcaseGamingPalace;